/**
 * Headline Service
 *
 * Centralized service for building and rendering headline content.
 *
 * Usage:
 *   import { HeadlineService } from './services/HeadlineService.js';
 *
 *   const legacyData = HeadlineService.buildFromCalculation(calcResult, context);
 *   HeadlineService.renderPortfolioHeadline(elements, config, legacyData, calculatorResult);
 *   HeadlineService.renderClassHeadline(elements, config, legacyData, classKey, classData);
 */

import { HeadlineFormatter } from '../formatters/HeadlineFormatter.js';
import { aggregateGrantValues } from '../helpers/vesting-utils.js';

const HeadlineService = {
	/**
	 * Build legacyData from a calculator result + per-render context.
	 * Single entry point for the legacyData shape consumed by HeadlineFormatter.
	 *
	 * @param {Object} calcResult - window.lastCalculationResult (Phase 2+ shape)
	 * @param {Object} context - Per-render legacy maps + labels + DOM context
	 * @param {string[]} context.shareKeys
	 * @param {Object} [context.userShares] - userData.clients[client].shares (used to
	 *   derive totalOwned/totalCommitted)
	 * @param {Object} context.bases
	 * @param {Object} context.latestValue
	 * @param {Object} context.distribs
	 * @param {Object} context.contributions
	 * @param {Object} context.returnsOfCapital
	 * @param {string} context.labelPaidIn
	 * @param {boolean} context.itsaFund
	 * @param {string} context.valuationDate
	 * @param {string} [context.currentClassValuationDate]
	 * @param {string} [context.stockStatus]
	 * @param {Object} [context.userData]
	 * @param {Object} [context.clientData]
	 * @param {string} [context.client]
	 * @returns {Object} - Legacy data object for HeadlineFormatter
	 */
	buildFromCalculation: function (calcResult, context) {
		// Derive totals from calcResult; calcResult is always present in
		// production but defaults are sane for race-window or null callers.
		const totalPaidInCapital = calcResult?.totalPaidInCapital || 0;
		const totalCurrentValue = calcResult?.totalCapitalAccount || calcResult?.totalValue || 0;
		const totalDistributions = calcResult?.totalDistributions || 0;
		const totalCapitalAccount = calcResult?.totalCapitalAccount || 0;

		const {
			bases = {},
			latestValue = {},
			distribs = {},
			contributions = {},
			returnsOfCapital = {},
			shareKeys = [],
			labelPaidIn = 'Paid-In Capital',
			itsaFund = false,
			valuationDate = '',
			currentClassValuationDate = '',
			stockStatus = null,
			userData = null,
			clientData = null,
			client = '',
			userShares,
		} = context;

		// Derive totalOwned/totalCommitted from userShares.
		// Three render paths previously each ran identical loops; now centralized.
		let totalOwned = 0;
		let totalCommitted = 0;
		if (userShares && shareKeys) {
			shareKeys.forEach(function (k) {
				totalOwned += Number(userShares[k]?.owned || 0);
				totalCommitted += Number(userShares[k]?.committed || 0);
			});
		}

		const result = {
			totalPaidInCapital,
			totalCurrentValue,
			totalDistributions,
			totalCapitalAccount,
			netValue: totalCurrentValue - totalPaidInCapital,
			bases,
			latestValue,
			distribs,
			contributions,
			returnsOfCapital,
			shareKeys,
			labelPaidIn,
			itsaFund,
			valuationDate,
			currentClassValuationDate,
		};

		// Add stock status eligibility data if provided
		if (stockStatus) {
			result.stockStatus = stockStatus;
			result.totalOwned = totalOwned;
			result.totalCommitted = totalCommitted;
		}

		// Add context for path-based variable resolution
		if (userData) result.userData = userData;
		if (clientData) result.clientData = clientData;
		if (client) result.client = client;

		// Aggregate grant values from vesting schedules (BW-style)
		if (userData && client && shareKeys.length) {
			const grantValues = aggregateGrantValues(userData, client, shareKeys);
			if (grantValues.totalOriginalGrantValue > 0) {
				result.totalOriginalGrantValue = grantValues.totalOriginalGrantValue;
			}
			if (grantValues.totalForecastedValue > 0) {
				result.totalForecastedValue = grantValues.totalForecastedValue;
			}
			if (grantValues.totalActualGrantValue > 0) {
				result.totalActualGrantValue = grantValues.totalActualGrantValue;
			}
		}

		return result;
	},

	/**
	 * Render a portfolio-level headline to DOM elements
	 *
	 * @param {Object} elements - DOM elements to update
	 * @param {HTMLElement} elements.label - Main headline label element
	 * @param {HTMLElement} elements.amount - Amount element (cleared for template headlines)
	 * @param {HTMLElement} elements.subtitle - Subtitle/date element
	 * @param {HTMLElement} [elements.tertiary] - Tertiary/third line element
	 * @param {Object} config - Template configuration
	 * @param {string} config.template - Headline template
	 * @param {string} [config.subtitleTemplate] - Subtitle template
	 * @param {string} [config.tertiaryTemplate] - Tertiary line template
	 * @param {Object} legacyData - Legacy data from buildFromCalculation
	 * @param {Object} [calculatorResult] - Calculator result if available
	 * @returns {Object} - { handled: boolean, headline: { main, subtitle, tertiary } }
	 */
	renderPortfolioHeadline: function (elements, config, legacyData, calculatorResult) {
		const { label, amount, subtitle, tertiary } = elements;

		if (!label) {
			return { handled: false, headline: null };
		}

		const headlineConfig = {
			template: config.template,
			subtitleTemplate: config.subtitleTemplate || '',
			tertiaryTemplate: config.tertiaryTemplate || '',
		};

		const headline = HeadlineFormatter.buildUnifiedHeadline(headlineConfig, {
			calculatorResult: calculatorResult,
			legacyData: legacyData,
		});

		// Check if headline has meaningful value
		const calcTotalValue = calculatorResult?.totalValue || 0;
		const calcVestedValue = calculatorResult?.vestedValue || 0;
		const calcUnvestedValue = calculatorResult?.unvestedValue || 0;
		const hasMeaningfulValue =
			legacyData.totalPaidInCapital > 0 || legacyData.totalCurrentValue > 0 || calcTotalValue > 0 || calcVestedValue > 0 || calcUnvestedValue > 0;

		if (headline.main && hasMeaningfulValue) {
			label.innerHTML = headline.main;
			if (amount) amount.innerHTML = '';
			if (subtitle) {
				subtitle.innerHTML = headline.subtitle ? `<div class="text-base font-normal mt-1 mb-1 text-secondary">${headline.subtitle}</div>` : '';
			}
			if (tertiary) {
				tertiary.innerHTML = headline.tertiary ? `<div class="text-base font-normal mt-1 mb-1 text-secondary">${headline.tertiary}</div>` : '';
			}
			return { handled: true, headline };
		}

		return { handled: false, headline };
	},

	/**
	 * Render a class-level headline to DOM elements
	 *
	 * @param {Object} elements - DOM elements to update
	 * @param {HTMLElement} elements.label - Main headline label element
	 * @param {HTMLElement} elements.amount - Amount element (cleared for template headlines)
	 * @param {HTMLElement} elements.subtitle - Subtitle/date element
	 * @param {HTMLElement} [elements.tertiary] - Tertiary/third line element
	 * @param {Object} config - Template configuration
	 * @param {string} config.classTemplate - Class headline template
	 * @param {string} [config.classSubtitleTemplate] - Class subtitle template
	 * @param {string} [config.classTertiaryTemplate] - Class tertiary template
	 * @param {Object} legacyData - Legacy data from buildFromCalculation
	 * @param {string} classKey - Current share class key
	 * @param {Object} [classData] - Class data from calculator breakdown
	 * @param {Object} [equityConfig] - Equity configuration for this class
	 * @returns {Object} - { handled: boolean, headline: { main, subtitle, tertiary } }
	 */
	renderClassHeadline: function (elements, config, legacyData, classKey, classData, equityConfig, aggregates) {
		const { label, amount, subtitle, tertiary } = elements;

		if (!label) {
			return { handled: false, headline: null };
		}

		const headlineConfig = {
			classTemplate: config.classTemplate,
			classSubtitleTemplate: config.classSubtitleTemplate || '',
			classTertiaryTemplate: config.classTertiaryTemplate || '',
		};

		const equityName = equityConfig?.name || classKey;
		const currentLabel = equityConfig?.currentLabel || '';

		const headline = HeadlineFormatter.buildUnifiedClassHeadline(headlineConfig, {
			classData: classData,
			legacyData: legacyData,
			classKey: classKey,
			equityName: equityName,
			currentLabel: currentLabel,
			aggregates: aggregates,
		});

		// Check if headline has meaningful value
		const classValue = classData?.value || legacyData.latestValue?.[classKey] || 0;
		const classBasis = legacyData.bases?.[classKey] || 0;
		const vestedValue = classData?.vestedValue || 0;
		const unvestedValue = classData?.unvestedValue || 0;

		if (headline.main && (classValue > 0 || classBasis > 0 || vestedValue > 0 || unvestedValue > 0)) {
			label.innerHTML = headline.main;
			if (amount) amount.innerHTML = '';
			if (subtitle) {
				subtitle.innerHTML = headline.subtitle ? `<div class="text-base font-normal mt-1 mb-1 text-secondary">${headline.subtitle}</div>` : '';
			}
			if (tertiary) {
				tertiary.innerHTML = headline.tertiary ? `<div class="text-base font-normal mt-1 mb-1 text-secondary">${headline.tertiary}</div>` : '';
			}
			return { handled: true, headline };
		}

		return { handled: false, headline };
	},

	/**
	 * Single headline render entry point.
	 *
	 * Builds legacyData from the calculator result + context, reads template
	 * config from clientData.language, and dispatches to renderPortfolioHeadline
	 * or renderClassHeadline based on context.viewMode. Replaces the three
	 * inline headline-render blocks in renderEquityData (#5198 Phase 7a).
	 *
	 * @param {Object} elements - DOM elements (label, amount, subtitle, tertiary)
	 * @param {Object} calcResult - window.lastCalculationResult
	 * @param {Object} context - Render context
	 * @param {string} context.viewMode - 'portfolio' or 'class'
	 * @param {string} [context.classKey] - Required when viewMode === 'class'
	 * @param {Object} [context.classData] - Class breakdown entry from calc result
	 * @param {Object} [context.equityConfig] - clientData.equity[classKey]
	 * @param {Object} context.clientData - For language.headline_template / class_headline_template
	 *   plus passed to buildFromCalculation for path-based variable resolution
	 * @param {string} [context.defaultTemplate] - Fallback portfolio template when
	 *   clientData.language.headline_template is not configured
	 *
	 * Plus the fields buildFromCalculation accepts (shareKeys, userShares,
	 * bases, latestValue, distribs, contributions, returnsOfCapital,
	 * labelPaidIn, itsaFund, valuationDate, currentClassValuationDate,
	 * stockStatus, userData, client).
	 *
	 * @returns {Object} - { handled: boolean, headline: { main, subtitle, tertiary } }
	 */
	renderHeadline: function (elements, calcResult, context) {
		const legacyData = this.buildFromCalculation(calcResult, context);
		const language = context.clientData?.language || {};

		if (context.viewMode === 'class') {
			const headlineConfig = {
				classTemplate: context.equityConfig?.class_headline_template || language.class_headline_template || language.headline_template,
				classSubtitleTemplate:
					context.equityConfig?.class_subtitle_template || language.class_subtitle_template || language.headline_subtitle_template || '',
				classTertiaryTemplate: language.class_tertiary_template || language.headline_tertiary_template || '',
			};
			return this.renderClassHeadline(
				elements,
				headlineConfig,
				legacyData,
				context.classKey,
				context.classData,
				context.equityConfig,
				context.classAggregates
			);
		}

		const headlineConfig = {
			template: language.headline_template || context.defaultTemplate,
			subtitleTemplate: language.headline_subtitle_template || '',
			tertiaryTemplate: language.headline_tertiary_template || '',
		};
		return this.renderPortfolioHeadline(elements, headlineConfig, legacyData, calcResult);
	},

	/**
	 * Render headline to graph view elements (tradingData section)
	 * Used when graph is displayed instead of landing page
	 *
	 * @param {Object} headline - { main, subtitle, tertiary } from HeadlineFormatter
	 */
	renderToGraphElements: function (headline) {
		const currentLabel = document.getElementById('currentLabel');
		const currentSymbol = document.getElementById('currentSymbol');
		const currentValue = document.getElementById('currentValue');
		const priceLabel = document.getElementById('priceLabel');
		const lastTraded = document.getElementById('lastTraded');
		const priceLabelReport = document.getElementById('priceLabelReport');
		const tertiaryLine = document.getElementById('headlineTertiary');

		if (currentLabel && currentSymbol && currentValue) {
			currentLabel.innerHTML = '';
			currentSymbol.innerHTML = headline.main;
			currentValue.innerHTML = '';
		}

		// Always update subtitle elements when template system is handling the headline
		// Hide the "Most recent Price" line if no subtitle, show subtitle if present
		if (priceLabel && lastTraded) {
			if (headline.subtitle) {
				priceLabel.innerHTML = '';
				lastTraded.innerHTML = headline.subtitle;
				if (priceLabelReport) priceLabelReport.classList.remove('hidden');
			} else {
				// Hide the price label line when there's no subtitle
				if (priceLabelReport) priceLabelReport.classList.add('hidden');
			}
		}

		// Render tertiary line if element exists and content is present
		if (tertiaryLine) {
			tertiaryLine.innerHTML = headline.tertiary || '';
			tertiaryLine.classList.toggle('hidden', !headline.tertiary);
		}
	},
};

export { HeadlineService };
